import React, { createContext, useCallback, useEffect, useReducer, useRef } from "react";
import api from "../../utils/axiosConfig";
import { handleApiError } from "../../utils/errorUtils";
import { fetchWithRetry } from "../../utils/apiUtils";

export const UserContext = createContext();

// API_BASE_URL kaldırıldı, direkt /api kullanacağız

// Action Types
const ACTIONS = {
  SET_USERS: "SET_USERS",
  SET_CURRENT_USER: "SET_CURRENT_USER",
  SET_SETTINGS: "SET_SETTINGS",
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  LOGOUT: "LOGOUT",
  SET_USER_SETTINGS: "SET_USER_SETTINGS",
};

// Initial State
const initialState = {
  users: [],
  currentUser: JSON.parse(localStorage.getItem("currentUser")) || null,
  userSettings: JSON.parse(localStorage.getItem("userSettings")) || { ayar14: false, ayar18: false },
  show18Ayar: JSON.parse(localStorage.getItem("show18Ayar")) ?? true,
  show14Ayar: JSON.parse(localStorage.getItem("show14Ayar")) ?? true,
  loading: false,
  error: null,
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_USERS:
      return { ...state, users: action.payload };
    case ACTIONS.SET_CURRENT_USER:
      if (action.payload) {
        localStorage.setItem("currentUser", JSON.stringify(action.payload));
      }
      return { ...state, currentUser: action.payload };
    case ACTIONS.SET_SETTINGS:
      localStorage.setItem("show18Ayar", JSON.stringify(action.payload?.ayar18 ?? true));
      localStorage.setItem("show14Ayar", JSON.stringify(action.payload?.ayar14 ?? true));
      localStorage.setItem("userSettings", JSON.stringify(action.payload));
      return {
        ...state,
        userSettings: action.payload,
        show18Ayar: action.payload?.ayar18 ?? true,
        show14Ayar: action.payload?.ayar14 ?? true,
      };
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload };
    case ACTIONS.LOGOUT:
      return { ...initialState, users: state.users };
    case ACTIONS.SET_USER_SETTINGS:
      localStorage.setItem("userSettings", JSON.stringify(action.payload));
      localStorage.setItem("show18Ayar", JSON.stringify(action.payload?.ayar18 ?? false));
      localStorage.setItem("show14Ayar", JSON.stringify(action.payload?.ayar14 ?? false));
      return {
        ...state,
        userSettings: action.payload,
        show18Ayar: action.payload?.ayar18 ?? false,
        show14Ayar: action.payload?.ayar14 ?? false,
      };
    default:
      return state;
  }
};

export const UserProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const fetchingRef = useRef(false);

  // Fetch all users
  const fetchUsers = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      // Token yoksa sessizce çık, hata mesajı gösterme
      return;
    }

    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const response = await api.get(`/api/users`);
      dispatch({ type: ACTIONS.SET_USERS, payload: Array.isArray(response.data) ? response.data : [] });
    } catch (error) {
      console.error('Kullanıcıları getirirken hata oluştu:', error);
      // Hata mesajını kullanıcıya göster veya logla
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, []);

  // Fetch user settings
  const fetchUserSettings = useCallback(async (userId) => {
    if (!userId) {
      console.warn("fetchUserSettings: userId bulunamadı");
      return;
    }
    
    // Aynı userId için birden fazla çağrıyı önle
    if (fetchingRef.current) {
      console.log("fetchUserSettings: Zaten yükleniyor, tekrar çağrılmayacak");
      return;
    }
    
    console.log("fetchUserSettings çağrıldı, userId:", userId);
    fetchingRef.current = true;
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.warn("fetchUserSettings: Token bulunamadı");
        return;
      }

      console.log("API isteği gönderiliyor:", `/api/settings?id=${userId}`);
      const response = await fetchWithRetry(`/api/settings?id=${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("API yanıtı:", response);

      // Yanıtın doğruluğunu kontrol ediyoruz:
      if (!response || !response.data) {
        console.warn("Kullanıcı ayarları verisi alınamadı. Response:", response);
        return;
      }

      console.log("Kullanıcı ayarları başarıyla alındı:", response.data);
      dispatch({ type: ACTIONS.SET_SETTINGS, payload: response.data });
      return response.data;
    } catch (err) {
      console.error("fetchUserSettings hatası:", err);
      console.error("Hata detayları:", {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data
      });
      handleApiError(err, (message) => dispatch({ type: ACTIONS.SET_ERROR, payload: message }));
    } finally {
      fetchingRef.current = false;
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, []);

  // Add new user
  const addUser = useCallback(async (user) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const response = await api.post(`/api/users`, user);
      fetchUsers(); // Refresh user list after adding
      return response.data;
    } catch (err) {
      handleApiError(err, (message) => dispatch({ type: ACTIONS.SET_ERROR, payload: message }));
      throw err; // Rethrow to handle in the UI
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, [fetchUsers]);

  // Login user
  const loginUser = async (email, password) => {
    try {
      const response = await api.post(`/api/login`, {
        email,
        password
      });

      const { token, user, remainingDays } = response.data;

      if (!token || !user) {
        throw new Error('Geçersiz sunucu yanıtı');
      }

      const userWithDays = {
        ...user,
        remainingDays: remainingDays ?? user.remainingDays,
      };

      // Token'ı ve kullanıcı bilgilerini kaydet
      localStorage.setItem('token', token);
      localStorage.setItem('currentUser', JSON.stringify(userWithDays));
      
      // API default header'ını ayarla
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Context state'ini güncelle
      dispatch({ type: ACTIONS.SET_CURRENT_USER, payload: userWithDays });
      
      // Kullanıcı ayarlarını getir - useEffect'te zaten çağrılıyor
      // if (user.id) {
      //   fetchUserSettings(user.id);
      // }
      
      return userWithDays;
    } catch (error) {
      const errData = error.response?.data;
      const serverError =
        (typeof errData === 'string' ? errData : errData?.error) ||
        error.message;
      console.error("Giriş hatası:", {
        status: error.response?.status,
        data: errData,
        message: error.message,
      });
      if (error.response?.status === 401) {
        throw new Error(serverError || 'Geçersiz email veya şifre');
      }
      if (error.response?.status === 403) {
        throw new Error(
          serverError ||
          'Erişim reddedildi (403). IIS güvenlik duvarı veya hesap kısıtı olabilir. Farklı tarayıcı deneyin veya yöneticiye başvurun.'
        );
      }
      throw new Error(serverError || 'Giriş işlemi sırasında bir hata oluştu');
    }
  };

  const validateSession = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return null;
    }

    try {
      const response = await api.get('/api/me');
      const user = response.data;
      dispatch({ type: ACTIONS.SET_CURRENT_USER, payload: user });
      return user;
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('currentUser');
        delete api.defaults.headers.common['Authorization'];
        dispatch({ type: ACTIONS.LOGOUT });
      }
      return null;
    }
  }, []);

  // Delete user
  const deleteUser = useCallback(async (userId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      dispatch({ type: ACTIONS.SET_ERROR, payload: 'Yetkilendirme tokeni bulunamadı' });
      throw new Error('Yetkilendirme tokeni bulunamadı. Lütfen tekrar giriş yapın.');
    }

    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    
    try {
      const response = await api.post(`/api/admin-operations`, {
        operation: 'delete-user',
        userId: String(userId),
      });
      
      console.log('DELETE yanıtı:', {
        status: response.status,
        data: response.data,
        headers: response.headers
      });
      
      fetchUsers(); // Refresh user list after deletion
      return response.data;
    } catch (err) {
      console.error('deleteUser HATASI:', err);
      console.error('Hata detayları:', {
        name: err.name,
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        headers: err.config?.headers,
        requestConfig: {
          method: err.config?.method,
          url: err.config?.url,
          headers: err.config?.headers
        }
      });
      
      // Daha spesifik hata mesajları
      if (err.response) {
        const serverMessage = err.response.data?.error;
        switch (err.response.status) {
          case 401:
            dispatch({ type: ACTIONS.SET_ERROR, payload: serverMessage || 'Oturumunuz sona erdi. Lütfen tekrar giriş yapın.' });
            break;
          case 403:
            dispatch({ type: ACTIONS.SET_ERROR, payload: serverMessage || 'Admin yetkisi gerekli. Admin hesabıyla tekrar giriş yapın.' });
            break;
          case 404:
            dispatch({ type: ACTIONS.SET_ERROR, payload: 'Kullanıcı bulunamadı' });
            break;
          case 405:
            dispatch({ type: ACTIONS.SET_ERROR, payload: 'Sunucu bu işlemi desteklemiyor' });
            break;
          default:
            handleApiError(err, (message) => dispatch({ type: ACTIONS.SET_ERROR, payload: message }));
        }
      } else if (err.request) {
        // İstek gönderildi ama yanıt alınamadı
        dispatch({ type: ACTIONS.SET_ERROR, payload: 'Sunucudan yanıt alınamadı' });
      } else {
        // İstek bile gönderilemedi
        dispatch({ type: ACTIONS.SET_ERROR, payload: 'İstek gönderilemedi' });
      }
      
      throw err; // UI'da yakalanması için tekrar fırlat
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, [fetchUsers]);

  // Aktif oturumları getir (userId -> oturum sayısı)
  const fetchUserSessions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return {};

    try {
      const response = await api.get('/api/login');
      const sessionsByUser = response.data || {};
      const counts = {};

      Object.entries(sessionsByUser).forEach(([userId, sessions]) => {
        counts[userId] = Array.isArray(sessions) ? sessions.length : 0;
      });

      return counts;
    } catch (error) {
      console.error('Oturum verileri alınamadı:', error);
      return {};
    }
  }, []);

  const clearAllSessions = useCallback(async () => {
    const response = await api.post('/api/admin-operations', { operation: 'clear-sessions' });
    return response.data;
  }, []);

  const clearUserSessions = useCallback(async (userId) => {
    const response = await api.post('/api/admin-operations', {
      operation: 'clear-sessions',
      userId: String(userId),
    });
    return response.data;
  }, []);

  const removeOneUserSession = useCallback(async (userId) => {
    const response = await api.post('/api/admin-operations', {
      operation: 'remove-one-session',
      userId: String(userId),
    });
    return response.data;
  }, []);

  // Update user
  const updateUser = useCallback(async (userId, updateData) => {
    dispatch({ type: ACTIONS.SET_LOADING, payload: true });
    try {
      const response = await api.post(`/api/update-user`, {
        userId: userId,
        updateData: updateData
      });
      fetchUsers(); // Refresh user list after updating
      return response.data;
    } catch (err) {
      handleApiError(err, (message) => dispatch({ type: ACTIONS.SET_ERROR, payload: message }));
      throw err; // Rethrow to handle in the UI
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  }, [fetchUsers]);

  // Logout user
  const logoutUser = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      
      // API'ye logout isteği gönder
      if (token) {
        await api.delete('/api/login', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        console.log('Logout API isteği başarılı');
      }
    } catch (error) {
      console.error('Logout API hatası:', error);
      // API hatası olsa bile local logout yap
    } finally {
      // Her durumda local storage'ı temizle
      localStorage.removeItem("currentUser");
      localStorage.removeItem("token");
      delete api.defaults.headers.common["Authorization"];
      dispatch({ type: ACTIONS.LOGOUT });
    }
  }, []);

  // Yeni setUserSettings fonksiyonu
  const setUserSettings = useCallback((settings) => {
    dispatch({ type: ACTIONS.SET_USER_SETTINGS, payload: settings });
    localStorage.setItem("userSettings", JSON.stringify(settings));
  }, []);

  useEffect(() => {
    const init = async () => {
      const user = await validateSession();
      if (user?.id) {
        fetchUserSettings(user.id);
        fetchUsers();
      }
    };
    init();
  }, [validateSession, fetchUserSettings, fetchUsers]);

  const value = {
    ...state,
    fetchUsers,
    fetchUserSettings,
    addUser,
    loginUser,
    deleteUser,
    updateUser,
    logoutUser,
    setUserSettings,
    validateSession,
    fetchUserSessions,
    clearAllSessions,
    clearUserSessions,
    removeOneUserSession,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => {
  const context = React.useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserContext must be used within a UserProvider');
  }
  return context;
};
